import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import DeveloperNavigation from '../components/DeveloperNavigation';
import { getGmailApiUrl } from '../utils/apiUrls';

const GMAIL_URL = getGmailApiUrl();

const COLUMN_ORDER = [
    'actions',
    'sent_at_utc',
    'to_email',
    'subject',
    'contract_id',
    'email_type',
    'message_id',
    'db_id',
    'error',
    'created_at'
];

const Modal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;
    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 2000
        }}>
            <div style={{
                backgroundColor: '#fff', borderRadius: '8px', padding: '24px',
                width: '90%', maxWidth: '900px', maxHeight: '90%',
                display: 'flex', flexDirection: 'column', position: 'relative',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 style={{ margin: 0 }}>{title}</h3>
                    <button onClick={onClose} style={{
                        background: 'none', border: 'none', fontSize: '1.5rem',
                        cursor: 'pointer', color: '#6b7280'
                    }}>×</button>
                </div>
                <div style={{ flex: 1, overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '4px' }}>
                    {children}
                </div>
            </div>
        </div>
    );
};

function DeveloperSentEmails() {
    const { user, isAuthenticated, loginWithRedirect } = useAuth0();
    const navigate = useNavigate();

    const [pageSize, setPageSize] = useState(20);
    const [emails, setEmails] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Modal State
    const [selectedEmail, setSelectedEmail] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Filters
    const [toEmail, setToEmail] = useState('');
    const [contractId, setContractId] = useState('');
    const [emailType, setEmailType] = useState('');
    const [dateStart, setDateStart] = useState('');
    const [dateEnd, setDateEnd] = useState('');

    const isKeyFloUser = useMemo(() => {
        const email = user?.email || '';
        const ALLOWED_USERS = ['cole@keyflo.ai', 'james@keyflo.ai', 'flo-test1@keyflo.ai'];
        return ALLOWED_USERS.includes(email);
    }, [user]);

    useEffect(() => {
        if (!isAuthenticated) return;
        if (!isKeyFloUser) {
            navigate('/');
        }
    }, [isAuthenticated, isKeyFloUser, navigate]);

    const formatDateTime = (ts) => {
        try {
            if (!ts) return '';
            return new Date(ts).toLocaleString();
        } catch (e) {
            return ts || '';
        }
    };

    const handleViewEmail = (email) => {
        setSelectedEmail(email);
        setIsModalOpen(true);
    };

    const fetchSentEmails = async (filterOverrides = null) => {
        if (!isAuthenticated || !isKeyFloUser) return;
        try {
            setLoading(true);
            setError('');

            const filters = (
                filterOverrides
                && typeof filterOverrides === 'object'
                && !('nativeEvent' in filterOverrides)
            ) ? filterOverrides : {
                toEmail,
                contractId,
                emailType,
                dateStart,
                dateEnd,
            };

            const params = {
                user_email: user?.email || '',
                limit: pageSize,
            };

            if (filters.toEmail) params.to_email = filters.toEmail;
            if (filters.contractId) params.contract_id = filters.contractId;
            if (filters.emailType?.trim()) params.email_type = filters.emailType.trim();
            if (filters.dateStart) params.date_from = new Date(filters.dateStart).toISOString();
            if (filters.dateEnd) {
                const end = new Date(filters.dateEnd);
                end.setHours(23, 59, 59, 999);
                params.date_to = end.toISOString();
            }

            const response = await axios.get(`${GMAIL_URL}/api/sent-emails`, { params });

            if (response.data?.status !== 'success') {
                throw new Error(response.data?.message || 'Failed to fetch sent emails');
            }

            setEmails(response.data.emails || []);
        } catch (e) {
            setError(e.message || 'Failed to load sent emails');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isAuthenticated && isKeyFloUser) fetchSentEmails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, isKeyFloUser, pageSize]);

    if (!isAuthenticated) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column' }}>
                <p>Please log in</p>
                <button onClick={() => loginWithRedirect({
                    authorizationParams: {
                        prompt: 'login'
                    }
                })}>Log in</button>
            </div>
        );
    }
    if (!isKeyFloUser) return null;

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <h2>Developer • Sent Emails (Firestore)</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' }}>
                    <button className="back-button" onClick={() => navigate('/')}>← Back</button>
                    <DeveloperNavigation />
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '12px' }}>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>To Email</label>
                    <input value={toEmail} onChange={(e) => setToEmail(e.target.value)} placeholder="to_email" style={{ width: '100%' }} />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Contract ID</label>
                    <input value={contractId} onChange={(e) => setContractId(e.target.value)} placeholder="contract_id" style={{ width: '100%' }} />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Email Type</label>
                    <input value={emailType} onChange={(e) => setEmailType(e.target.value)} placeholder="email_type" style={{ width: '100%' }} />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Start Date</label>
                    <input type="date" value={dateStart} onChange={(e) => setDateStart(e.target.value)} style={{ width: '100%' }} />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>End Date</label>
                    <input type="date" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} style={{ width: '100%' }} />
                </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <button type="button" onClick={() => fetchSentEmails()}>Apply Filters</button>
                <button onClick={() => {
                    const empty = { toEmail: '', contractId: '', emailType: '', dateStart: '', dateEnd: '' };
                    setToEmail('');
                    setContractId('');
                    setEmailType('');
                    setDateStart('');
                    setDateEnd('');
                    fetchSentEmails(empty);
                }}>Clear</button>
            </div>

            {loading && <LoadingSpinner />}
            {error && <div style={{ color: 'red', marginBottom: '12px' }}>{error}</div>}

            <div style={{ overflowX: 'auto', background: '#ffffff', border: '1px solid #e5e7eb' }}>
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                    <thead>
                        <tr>
                            {COLUMN_ORDER.map((col) => (
                                <th key={col} style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc' }}>
                                    {col.replace(/_/g, ' ').toUpperCase()}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {emails.map((email, idx) => (
                            <tr key={email.db_id || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                {COLUMN_ORDER.map((col) => {
                                    if (col === 'actions') {
                                        return (
                                            <td key={col} style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>
                                                <button
                                                    onClick={() => handleViewEmail(email)}
                                                    style={{
                                                        padding: '4px 8px', fontSize: '0.75rem',
                                                        background: '#3b82f6', color: '#fff',
                                                        border: 'none', borderRadius: '4px', cursor: 'pointer'
                                                    }}
                                                >
                                                    View
                                                </button>
                                            </td>
                                        );
                                    }
                                    let val = email[col];
                                    if (col === 'to_email' && !val) val = email['to']; // Handle legacy 'to' field
                                    if (col === 'sent_at_utc' || col === 'created_at') val = formatDateTime(val);
                                    return (
                                        <td key={col} style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', fontSize: '0.9rem' }}>
                                            {String(val || '')}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        {emails.length === 0 && !loading && (
                            <tr>
                                <td colSpan={COLUMN_ORDER.length} style={{ padding: '12px', textAlign: 'center' }}>No sent emails found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={`Email Preview: ${selectedEmail?.subject || 'No Subject'}`}
            >
                {selectedEmail?.body_html ? (
                    <iframe
                        title="Email Content"
                        srcDoc={selectedEmail.body_html
                            // Base64 data URIs are stripped before Firestore storage (1MB limit)
                            // and replaced with the literal string [embedded-image].
                            // Swap them for a small grey placeholder so they don't show a broken icon.
                            .replace(
                                /src="?\[embedded-image\]"?/g,
                                'src="data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'120\' height=\'40\'%3E%3Crect width=\'120\' height=\'40\' rx=\'4\' fill=\'%23e5e7eb\'/%3E%3Ctext x=\'50%25\' y=\'55%25\' dominant-baseline=\'middle\' text-anchor=\'middle\' font-size=\'10\' fill=\'%236b7280\'%3Eimage%3C/text%3E%3C/svg%3E"'
                            )
                        }
                        style={{ width: '100%', height: '600px', border: 'none' }}
                    />
                ) : (selectedEmail?.body_text || selectedEmail?.body) ? (
                    <div style={{ padding: '16px', whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.9rem', lineHeight: '1.6' }}>
                        {selectedEmail?.body_text || selectedEmail?.body}
                    </div>
                ) : (
                    <div style={{ padding: '16px', color: '#6b7280', fontStyle: 'italic' }}>No content available</div>
                )}
            </Modal>
        </div>
    );
}

export default DeveloperSentEmails;
